'use client'

import PropTypes from 'prop-types'
import { MiniMatterCard } from '@repo/ui/MiniMatterCard'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import { CaseManagementIcon } from '@repo/ui/CaseManagementIcon'
import { LoadingState as LoadingSpinner } from '@repo/ui/LoadingState'
import { useMattersStore } from '@repo/store/useMattersStore'
import { EmptyState } from '@repo/ui/EmptyState'

const LoadingState = ({ minHeight, maxHeight }) => {
  return (
    <MUIStack
      data-testid="emptyState"
      direction="column"
      justifyContent="center"
      alignItems="center"
      style={{
        width: '100%',
        height: '100%',
        minHeight,
        maxHeight,
        overflowX: 'hidden',
        overflowY: 'hidden',
        paddingBottom: '50px'
      }}
    >
      <LoadingSpinner />
      <MUITypography variant="body1" sx={{ mt: 4 }}>
        Loading
      </MUITypography>
    </MUIStack>
  )
}

LoadingState.propTypes = {
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string
}

const ResultsState = ({
  matters,
  minHeight = '360px',
  maxHeight = '400px',
  clickHandler,
  filterOutCurrentMatter,
  disableDefaultClickHandler,
  sx,
  isEmailCard = false
}) => {
  const { currentClientMatterNo } = useMattersStore()

  return (
    <MUIStack
      data-testid="matterList"
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      tabIndex="0"
      style={{
        width: '100%',
        height: '100%',
        minHeight,
        maxHeight,
        overflowX: 'hidden',
        overflowY: 'auto',
        padding: isEmailCard ? '10px 0px' : '10px 12px',
        position: 'relative',
        ...sx
      }}
    >
      {matters
        ?.filter((matter) =>
          filterOutCurrentMatter ? `${matter.clientNo}-${matter.matterNo}` !== currentClientMatterNo : true
        )
        .map((matter, index) => {
          const {
            matterNo,
            clientNo,
            forename,
            surname,
            feeEarnerDescription,
            feeEarnerId,
            matterDescription,
            highRisk,
            dateArchived,
            isUserRestricted,
            workflowTypeId
          } = matter
          return (
            <MiniMatterCard
              key={`${clientNo}-${matterNo}-${index}`}
              forename={forename}
              surname={surname}
              clientNo={clientNo}
              matterNo={matterNo}
              matterDescription={matterDescription}
              feeEarnerDescription={feeEarnerDescription}
              feeEarnerId={feeEarnerId}
              highRisk={highRisk}
              dateArchived={dateArchived}
              clickHandler={clickHandler}
              disableDefaultClickHandler={disableDefaultClickHandler}
              isUserRestricted={isUserRestricted}
              workflowTypeId={workflowTypeId}
              isEmailCard={isEmailCard}
            />
          )
        })}
    </MUIStack>
  )
}

export const MatterList = ({
  matters,
  minHeight = '360px',
  maxHeight = '360px',
  isEmpty,
  isLoading,
  clickHandler,
  showLoadingState = true,
  filterOutCurrentMatter = false,
  disableDefaultClickHandler = false,
  sx = {},
  isEmailCard = false
}) => {
  if (showLoadingState && isLoading) return <LoadingState minHeight={minHeight} maxHeight={maxHeight} />
  if (isEmpty)
    return (
      <EmptyState
        text="No results"
        icon={<CaseManagementIcon sx={{ fontSize: 36, mb: 4 }} />}
        sx={{ minHeight, paddingBottom: '40px' }}
      />
    )

  return (
    <ResultsState
      matters={matters}
      minHeight={minHeight}
      maxHeight={maxHeight}
      clickHandler={clickHandler}
      filterOutCurrentMatter={filterOutCurrentMatter}
      disableDefaultClickHandler={disableDefaultClickHandler}
      sx={sx}
      isEmailCard={isEmailCard}
    />
  )
}

ResultsState.propTypes = {
  matters: PropTypes.array,
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string,
  clickHandler: PropTypes.func,
  filterOutCurrentMatter: PropTypes.bool,
  disableDefaultClickHandler: PropTypes.bool,
  sx: PropTypes.object,
  isEmailCard: PropTypes.bool
}

MatterList.propTypes = {
  matters: PropTypes.array,
  minHeight: PropTypes.string,
  maxHeight: PropTypes.string,
  isEmpty: PropTypes.bool,
  isLoading: PropTypes.bool,
  clickHandler: PropTypes.func,
  showLoadingState: PropTypes.bool,
  filterOutCurrentMatter: PropTypes.bool,
  disableDefaultClickHandler: PropTypes.bool,
  sx: PropTypes.object,
  isEmailCard: PropTypes.bool
}
